import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableColumnHeader,
  DataTableSkeleton,
  Input,
  useDataTable,
} from '@data-delivery/d3s';
import { productsQueries } from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export interface AssignmentProductsProps {
  products?: string[];
  businessGroupId?: string;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  sorting?: Array<{ id: string; desc: boolean }>;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  isEditMode?: boolean;
  onBulkAddProducts?: (productIds: string[]) => void;
  onBulkRemoveProducts?: (productIds: string[]) => void;
}

export function AssignmentProducts({
  products,
  businessGroupId,
  className,
  sorting,
  globalFilter,
  onGlobalFilterChange,
  isEditMode,
  onBulkAddProducts,
  onBulkRemoveProducts,
}: AssignmentProductsProps) {
  const navigate = useNavigate();
  const { data: allProducts = [], isLoading } = useQuery(productsQueries.list());

  const externalSorting = sorting ?? [];
  const globalFilterValue = globalFilter ?? '';

  const selectedProducts = useMemo(() => {
    if (!products || products.length === 0) {
      return [] as typeof allProducts;
    }

    const scope = businessGroupId
      ? allProducts.filter(p => p.businessGroup === businessGroupId)
      : allProducts;
    const onlySelected = scope.filter(p => products.includes(p.id));
    const searched = globalFilterValue.trim().length
      ? onlySelected.filter(p =>
          [p.id, p.name, p.crmCode, p.crmGroup]
            .filter(Boolean)
            .some(val => (val || '').toLowerCase().includes(globalFilterValue.toLowerCase())),
        )
      : onlySelected;
    return searched;
  }, [allProducts, products, businessGroupId, globalFilterValue]);

  type Row = (typeof selectedProducts)[number];
  const columns = useMemo<Array<ColumnDef<Row>>>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Product' />,
        cell({ row }) {
          const productId = row.original.id;
          const name = row.original.name as string | undefined;

          return (
            <button
              type='button'
              className='font-medium text-primary hover:underline'
              onClick={() => {
                if (!productId) {
                  return;
                }

                navigate(`/configuration/products/detail/${productId}`);
              }}
            >
              {name || productId}
            </button>
          );
        },
      },
      {
        accessorKey: 'crmCode',
        header: ({ column }) => <DataTableColumnHeader column={column} title='CRM Code' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'crmGroup',
        header: ({ column }) => <DataTableColumnHeader column={column} title='CRM Group' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'id',
        header: ({ column }) => <DataTableColumnHeader column={column} title='ID' />,
        cell: ({ getValue }) => (
          <span className='text-xs text-muted-foreground'>{getValue<string>()}</span>
        ),
        enableSorting: false,
      },
    ],
    [navigate],
  );

  const table = useDataTable<Row>({
    data: selectedProducts,
    columns,
    getRowId: r => r.id,
    initialState: { sorting: externalSorting },
  });

  const hasAnyProducts = Array.isArray(products) && products.length > 0;

  if (!hasAnyProducts) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader className='pb-3'>
        <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
          <div className='flex items-center gap-2'>
            <CardTitle className='text-sm font-medium'>Products ({products.length})</CardTitle>
          </div>
          <div className='flex gap-2 flex-wrap items-center'>
            <Input
              placeholder='Search products'
              value={globalFilterValue}
              disabled={isLoading}
              onChange={e => {
                const val = e.target.value;
                table.setGlobalFilter(val);
                onGlobalFilterChange?.(val);
              }}
              className='h-8 w-52 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
            />
            <Button
              variant='outline'
              size='sm'
              disabled={isLoading && !globalFilterValue}
              onClick={() => {
                table.setSorting([]);
                table.setGlobalFilter('');
                onGlobalFilterChange?.('');
              }}
            >
              Reset
            </Button>
            <Button
              variant='outline'
              size='sm'
              disabled={isLoading}
              onClick={() => {
                const rows = table.getRowModel().rows.map(r => r.original);
                const header = ['Product', 'CRM Code', 'CRM Group', 'ID'];
                const csvRows = rows.map(r => [
                  r.name ?? '',
                  r.crmCode ?? '',
                  r.crmGroup ?? '',
                  r.id,
                ]);
                const lines = [header, ...csvRows]
                  .map(cols =>
                    cols
                      .map(val => {
                        const str = String(val ?? '');
                        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
                      })
                      .join(','),
                  )
                  .join('\n');

                const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'assignment-products.csv';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className='flex flex-col gap-3'>
        {/* CSV import/remove is handled in the editor component */}
        {isLoading ? (
          <DataTableSkeleton
            withPagination={false}
            rowCount={8}
            columnCount={columns.length}
            searchableColumnCount={0}
            filterableColumnCount={0}
            showViewOptions={false}
            cellWidths={['12rem', '8rem', '8rem', '10rem']}
            shrinkZero
          />
        ) : (
          <div className='rounded-md border'>
            <DataTable
              table={table}
              className='h-[300px]'
              emptyPlaceholderTitle='Products'
              emptyPlaceholderDescription={
                selectedProducts.length === 0 && globalFilterValue
                  ? 'No matching products found.'
                  : 'No products found.'
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AssignmentProducts;
